import { useGetTinyArtworks } from "@/api/artwork/artwork";
import { useGenerateArtworksPdf } from "@/api/pdf";
import { useAdvancedSearch } from "@/context/advanced-search/AdvancedSearchContext";
import i18n, { SupportedLocale } from "@/i18n";
import { getDefaultImage } from "@/lib/image";
import { SelectValue } from "@radix-ui/react-select";
import { PropsWithChildren, useEffect, useId, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PrintDrawer from "../print/PrintDrawer";
import SelectedRecord from "../print/SelectedRecord";
import { Checkbox } from "../ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel } from "../ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import { Separator } from "../ui/separator";
import {
  ArtworkPrintSettings,
  binderTemplateFields,
  completeTemplateFields,
  simpleTemplateFields,
} from "./artworkPrintSettings";

type ArtworkPrintDrawerProps = PropsWithChildren<{
  selectedArtworksIds: string[];
}>;

type FormValues = {
  template: string;
  settings: ArtworkPrintSettings;
};

const ArtworkPrintDrawer = ({
  selectedArtworksIds,
}: ArtworkPrintDrawerProps) => {
  const { t } = useTranslation();
  const { prismaOrderByQueryContent } = useAdvancedSearch();

  const form = useForm<FormValues>({
    defaultValues: {
      template: "",
      settings: completeTemplateFields.reduce(
        (acc, field) => ({
          ...acc,
          [field]: false,
        }),
        {} as ArtworkPrintSettings,
      ),
    },
  });
  const { mutateAsync: generateArtworksPdf, isPending } =
    useGenerateArtworksPdf();
  const template = form.watch("template");
  const formId = useId();
  const [previewURL, setPreviewURL] = useState<string | null>(null);

  const options = useMemo(() => {
    if (template === "simple") return simpleTemplateFields;
    if (template === "complete") return completeTemplateFields;
    if (template === "binder") return binderTemplateFields;
    return [];
  }, [template]);

  useEffect(() => {
    form.resetField("settings");
  }, [options, form]);

  const onSubmit = async (data: FormValues) => {
    const file = await generateArtworksPdf({
      lang: i18n.language as SupportedLocale,
      template: data.template,
      artworkIds: selectedArtworksIds,
      settings: data.settings,
      orderBy: prismaOrderByQueryContent,
    });
    const url = URL.createObjectURL(file);
    setPreviewURL(url);
  };

  const { data: selectedArtworks = [] } = useGetTinyArtworks({
    where: {
      id: { in: selectedArtworksIds },
    },
    orderBy: prismaOrderByQueryContent,
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} id={formId}>
        <PrintDrawer
          formId={formId}
          submitButtonProps={{ disabled: !template, isLoading: isPending }}
          previewUrl={previewURL}
          setPreviewUrl={setPreviewURL}
        >
          <div className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name="template"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("printSelectModel")}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="simple">{t("simple")}</SelectItem>
                      <SelectItem value="complete">{t("complete")}</SelectItem>
                      <SelectItem value="binder">{t("classeur")}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <div className="space-y-4">
              <FormLabel>{t("printOptions")}</FormLabel>
              {options.map((option) => (
                <FormField
                  key={option}
                  control={form.control}
                  name={`settings.${option}`}
                  render={({ field }) => (
                    <FormItem className="flex flex-row gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value as boolean}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel>{t(option)}</FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>

          <Separator className="my-4" />

          <div className="flex flex-col gap-4">
            <span className="font-semibold font-[Raleway] text-base">
              {t("artworksIncluded")}
            </span>

            {selectedArtworks.map(
              ({ id, attachments, oeuvresDescriptionTitre }) => {
                const image = getDefaultImage(
                  attachments,
                  "artwork",
                  "publicUrl",
                );

                return (
                  <SelectedRecord
                    key={id}
                    image={image}
                    title={oeuvresDescriptionTitre}
                  />
                );
              },
            )}
          </div>
        </PrintDrawer>
      </form>
    </Form>
  );
};

export default ArtworkPrintDrawer;
